class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => {
      this.openTimer = null;
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty') && !this.addPending
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    clearTimeout(this.openTimer);
    this.openTimer = null;
    if (this.addPending) this.addDismissed = true;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  beginAdd(trigger, details = {}) {
    if (this.addPending) return;
    this.addPending = true;
    this.addDismissed = false;
    this.classList.add('cup-cart-is-adding');
    this.pendingInertElements = [this.querySelector('cart-drawer-items'), this.querySelector('.drawer__footer')]
      .filter(Boolean).map((element) => ({ element, inert: element.inert }));
    this.pendingInertElements.forEach(({element}) => { element.inert = true; });
    const panel = document.createElement('div');
    panel.className = 'cup-cart-add-status';
    panel.setAttribute('aria-busy', 'true');
    const message = document.createElement('p');
    message.setAttribute('role', 'status');
    message.className = 'cup-cart-add-status__message';
    message.textContent = details.preparingLabel || trigger?.textContent?.trim() || '';
    panel.appendChild(message);
    const card = document.createElement('div');
    card.className = 'cup-cart-add-status__card';
    const preview = document.createElement('div');
    preview.className = 'cup-cart-add-preview';
    preview.setAttribute('aria-busy', 'true');
    const placeholder = document.createElement('span');
    placeholder.textContent = details.previewLabel || '';
    preview.appendChild(placeholder);
    const content = document.createElement('div');
    content.className = 'cup-cart-add-status__details';
    const title = document.createElement('h3');
    title.textContent = details.title || '';
    const price = document.createElement('p');
    price.className = 'cup-cart-add-status__price';
    price.textContent = details.price || '';
    content.append(title, price);
    const properties = document.createElement('dl');
    properties.className = 'cup-cart-add-status__properties';
    for (const property of details.properties || []) {
      const row = document.createElement('div');
      const label = document.createElement('dt');
      const value = document.createElement('dd');
      label.textContent = `${property.label}:`;
      value.textContent = property.value;
      row.append(label, value);
      properties.appendChild(row);
    }
    content.appendChild(properties);
    card.append(preview, content);
    panel.appendChild(card);
    this.querySelector('.drawer__inner').insertBefore(panel, this.querySelector('cart-drawer-items'));
    this.pendingAddPanel = panel;
    this.open(trigger);
    return panel;
  }

  updateAddStatus(message, panel = this.pendingAddPanel) {
    if (!this.addPending || panel !== this.pendingAddPanel || !panel?.isConnected) return;
    panel.querySelector('[role="status"]').textContent = message;
  }

  updateAddProgress(fraction, panel = this.pendingAddPanel) {
    if (!this.addPending || panel !== this.pendingAddPanel || !panel?.isConnected) return;
    let bar = panel.querySelector('.cup-cart-add-progress');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'cup-cart-add-progress';
      bar.setAttribute('role', 'progressbar');
      bar.setAttribute('aria-valuemin', '0');
      bar.setAttribute('aria-valuemax', '100');
      bar.style.cssText = 'height:6px;margin:.8rem 0 1.6rem;border-radius:3px;overflow:hidden;background:rgba(var(--color-foreground),.12)';
      const fill = document.createElement('span');
      fill.style.cssText = 'display:block;width:0;height:100%;border-radius:inherit;background:rgb(var(--color-foreground));transition:width .3s ease';
      bar.appendChild(fill);
      panel.querySelector('[role="status"]').after(bar);
    }
    const percent = Math.max(0, Math.min(100, Math.round(fraction * 100)));
    bar.setAttribute('aria-valuenow', String(percent));
    bar.firstElementChild.style.width = `${percent}%`;
  }

  updateAddPreview(file, panel = this.pendingAddPanel) {
    if (!(file instanceof Blob) || !this.addPending || panel !== this.pendingAddPanel || !panel?.isConnected) return;
    if (this.pendingPreviewUrl) URL.revokeObjectURL(this.pendingPreviewUrl);
    const url = URL.createObjectURL(file);
    this.pendingPreviewUrl = url;
    const preview = panel.querySelector('.cup-cart-add-preview');
    const image = new Image();
    image.alt = preview.textContent;
    image.decoding = 'async';
    image.onload = () => {
      if (panel !== this.pendingAddPanel || !panel.isConnected || this.pendingPreviewUrl !== url) return;
      preview.replaceChildren(image);
      preview.setAttribute('aria-busy', 'false');
    };
    image.src = url;
  }

  finishAdd() {
    this.querySelector('.cup-cart-add-status')?.remove();
    this.pendingAddPanel = null;
    if (this.pendingPreviewUrl) URL.revokeObjectURL(this.pendingPreviewUrl);
    this.pendingPreviewUrl = null;
    this.pendingInertElements?.forEach(({element, inert}) => { element.inert = inert; });
    this.pendingInertElements = null;
    this.classList.remove('cup-cart-is-adding');
    this.addPending = false;
    if (this.classList.contains('active')) {
      const inner = this.querySelector('.drawer__inner');
      trapFocus(inner, inner);
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const wasPending = this.addPending;
    const updates = this.getSectionsToRender().map((section) => {
      const element = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const html = parsedState.sections?.[section.id];
      const content = typeof html === 'string' ? this.getSectionDOM(html, section.selector) : null;
      return { element, content };
    });
    if (updates.some(({element, content}) => element && !content)) {
      this.finishAdd();
      window.location.assign(window.routes.cart_url);
      return;
    }
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    updates.forEach(({element, content}) => {
      if (element && content) element.innerHTML = content.innerHTML;
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      const wasDismissed = this.addDismissed;
      this.finishAdd();
      if (!wasPending || !wasDismissed) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
